import { useRequiredContext } from "@redotech/react-util/context";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { SubscriptionIntegration } from "@redotech/redo-model/subscription-integrations/subscription-integration";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const subscriptionKeys = {
  all: (customerEmail: string) => ["subscriptions", customerEmail] as const,
  detail: (subscriptionId: number) =>
    ["subscriptions", "detail", subscriptionId] as const,
};

export function useCustomerSubscriptions(customerEmail: string | undefined) {
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);

  return useQuery({
    queryKey: subscriptionKeys.all(customerEmail || ""),
    queryFn: async () => {
      if (!customerEmail) {
        return {
          subscriptions: [],
          totalCount: 0,
          totalSpent: "0.00",
          totalCharges: 0,
          hasSubscriptionIntegration: false,
        };
      }
      const result = await supportRpcClient.getCustomerSubscriptions({
        customerEmail,
      });

      if (!result || result.subscriptions.length === 0) {
        return {
          subscriptions: [],
          totalCount: 0,
          totalSpent: "0.00",
          totalCharges: 0,
          hasSubscriptionIntegration: result.hasSubscriptionIntegration,
        };
      }

      const customerId = result.subscriptions[0]?.customerId;

      if (!customerId) {
        return { ...result, totalSpent: "0.00", totalCharges: 0 };
      }

      const totalSpentResult =
        await supportRpcClient.getCustomerSubscriptionTotalSpent({
          customerId,
        });

      return {
        ...result,
        totalSpent: totalSpentResult.totalSpent,
        totalCharges: totalSpentResult.totalCharges,
      };
    },
    enabled: !!customerEmail,
  });
}

export function useEditSubscription(customerEmail: string | undefined) {
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscriptionId,
      updates,
    }: {
      subscriptionId: number;
      updates: {
        quantity?: number;
        orderIntervalFrequency?: number;
        orderIntervalUnit?: "day" | "week" | "month";
        nextChargeScheduledAt?: string;
        price?: string;
      };
    }) => {
      const result = await supportRpcClient.editSubscription({
        subscriptionId,
        updates,
      });
      return result;
    },
    onSuccess: (data) => {
      if (!customerEmail) return;

      queryClient.setQueryData(
        subscriptionKeys.all(customerEmail),
        (
          old:
            | {
                subscriptions: SubscriptionIntegration[];
                totalCount: number;
                totalSpent: string;
                totalCharges: number;
              }
            | undefined,
        ) => {
          if (!old) return old;

          return {
            ...old,
            subscriptions: old.subscriptions.map((sub) =>
              sub.id === data.subscription.id ? data.subscription : sub,
            ),
          };
        },
      );

      queryClient.setQueryData(
        subscriptionKeys.detail(data.subscription.id),
        data.subscription,
      );
    },
  });
}

export function useCancelSubscription(customerEmail: string | undefined) {
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscriptionId,
      cancellationReason,
    }: {
      subscriptionId: number;
      cancellationReason?: string;
    }) => {
      const result = await supportRpcClient.cancelSubscription({
        subscriptionId,
        cancellationReason,
      });
      return result;
    },
    onSuccess: (data) => {
      if (!customerEmail) return;

      queryClient.setQueryData(
        subscriptionKeys.all(customerEmail),
        (
          old:
            | {
                subscriptions: SubscriptionIntegration[];
                totalCount: number;
                totalSpent: string;
                totalCharges: number;
              }
            | undefined,
        ) => {
          if (!old) return old;

          return {
            ...old,
            subscriptions: old.subscriptions.map((sub) =>
              sub.id === data.subscription.id ? data.subscription : sub,
            ),
          };
        },
      );

      queryClient.setQueryData(
        subscriptionKeys.detail(data.subscription.id),
        data.subscription,
      );
    },
  });
}

export function useSkipSubscription(customerEmail: string | undefined) {
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subscriptionId }: { subscriptionId: number }) => {
      const result = await supportRpcClient.skipSubscription({
        subscriptionId,
      });
      return result;
    },
    onSuccess: (data) => {
      if (!customerEmail || !data.subscription) return;

      queryClient.setQueryData(
        subscriptionKeys.all(customerEmail),
        (
          old:
            | {
                subscriptions: SubscriptionIntegration[];
                totalCount: number;
                totalSpent: string;
                totalCharges: number;
              }
            | undefined,
        ) => {
          if (!old) return old;

          return {
            ...old,
            subscriptions: old.subscriptions.map((sub) =>
              sub.id === data.subscription!.id ? data.subscription! : sub,
            ),
          };
        },
      );

      queryClient.setQueryData(
        subscriptionKeys.detail(data.subscription.id),
        data.subscription,
      );
    },
  });
}

export function useEditSubscriptionAddress(customerEmail: string | undefined) {
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscriptionId,
      address,
    }: {
      subscriptionId: number;
      address: {
        address1: string;
        address2?: string;
        city: string;
        province: string;
        zip: string;
        country: string;
      };
    }) => {
      const result = await supportRpcClient.editSubscriptionAddress({
        subscriptionId,
        address,
      });
      return result;
    },
    onSuccess: (data) => {
      if (!customerEmail) return;

      queryClient.setQueryData(
        subscriptionKeys.all(customerEmail),
        (
          old:
            | {
                subscriptions: SubscriptionIntegration[];
                totalCount: number;
                totalSpent: string;
                totalCharges: number;
              }
            | undefined,
        ) => {
          if (!old) return old;

          return {
            ...old,
            subscriptions: old.subscriptions.map((sub) =>
              sub.id === data.subscription.id ? data.subscription : sub,
            ),
          };
        },
      );

      queryClient.setQueryData(
        subscriptionKeys.detail(data.subscription.id),
        data.subscription,
      );
    },
  });
}
